<template>
  <div class="row input-section">
    <div class="col-md-12 col-lg-12 col-sm-12 ps-0">
      <div class="row g-2 p-2">
        <!-- Breadcrumbs -->
        <nav aria-label="breadcrumb">
          <ol class="mt-2 breadcrumb align-middle">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'networks-blog', params: { slug: network.slug } }" class="text-primary fw-bold">
                {{ $t('blog') }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <a class="text-secondary" style="pointer-events: none;">
                <strong v-if="this.$route.name == 'network-blog-post-create-page'">Create Post</strong>
                <strong v-else>Update Post</strong>
              </a>
            </li>
          </ol>
        </nav>
      </div>
    </div>
    <ValidationError v-if="postFormErrors" :errors="postFormErrors"></ValidationError>
    <div class="col-md-12 col-lg-12 col-sm-12">

      <div class="form-group py-2">
        <label for="title">{{ $t('network.networkForm.title') }}</label> <sup class="text-danger ms-1">*</sup>
        <input id="title" v-model="form.title" data-test="title" type="text" placeholder="Enter the title of this post" required class="form-control" />
        <small class="text-muted">
          This title will display as the header of the post.
        </small>
      </div>
      <div class="form-group py-2">
        <label for="author">Author</label>
        <input id="author" v-model="form.author" data-test="author" type="text" placeholder="Enter the author of this post" class="form-control" />
        <small class="text-muted">
          If empty, the creator of this post will be credited as the author.
        </small>
      </div>
      <div class="form-group py-2">
        <label for="">Cover photo</label>
        <div style="display: flex; justify-content: space-between;">
          <div :class="form.cover_image ? 'col-11' : 'col-12'">
            <input id="cover_image" data-test="cover_image" type="file" class="form-control" @change="uploadCoverImage" />
          </div>
          <div v-if="form.cover_image" class="col-1 text-end">
            <i class="fas fa-check-circle fa-2x mt-2 text-success"></i>
          </div>
        </div>
        <small class="text-muted">
          Only png and jpg formats are allowed. If empty, a randomly generated gradient will act as the cover.
        </small>
      </div>

      <div class="form-group py-2">
        <label for="">Content</label> <sup class="text-danger ms-1">*</sup>
        <editor
          v-model="form.content"
          required
          :api-key="tinyMceApiKey"
          :init="{
            height: 500,
            menubar: false,
            images_upload_handler: uploadImageTinyMceBlob,
            automatic_uploads: true,
            file_picker_types: 'image',
            browser_spellcheck: true,
            paste_as_text: true,
            relative_urls : false,
            remove_script_host : false,
            document_base_url : home,
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
             alignleft aligncenter alignright alignjustify | \
             bullist numlist outdent indent | removeformat | help | link image ',
            image_advtab: true,
            image_class_list: [
              { title: 'None', value: '' },
              { title: 'Rounded', value: 'rounded' },
              { title: 'Rounded (circle)', value: 'rounded-circle' },
              { title: 'Margin Right', value: 'mr-5' },
              { title: 'Picture', value: 'img-fluid img-thumbnail' },
              { title: 'Fluid', value: 'img-fluid' },
            ],
          }"
        />
      </div>

      <div class="form-group py-2">
        <span class="text-primary fw-bold" @click="showPostPreview">Show Preview</span>
      </div>
    </div>

    <div class="col-md-12 col-lg-12 col-sm-12 mt-4">
      <button id="schedule_post" data-test="schedule_post" :disabled="form.title.length === 0 || form.content.length === 0" class="btn btn-primary me-2" style="padding: 0.425rem 1.9rem !important;"
      @click="form.schedule_post = !form.schedule_post, showScheduleModal = !showScheduleModal">
        Schedule Post
      </button>
      <button id="publish_post" data-test="publish_post" :disabled="form.title.length === 0 || form.content.length === 0" class="btn btn-primary" style="padding: 0.425rem 1.9rem !important;" @click="submitRequest('send')">
        <div>Publish Post</div>
      </button>
    </div>

    <b-modal v-model="showScheduleModal" hide-footer hide-header>
      <div class="text-center">
        <date-range-picker
          id="sent_at_datetime"
          ref="picker"
          v-model="form.sent_at_datetime"
          data-test="sent_at_datetime"
          :locale-data="{ firstDay: 1, format: 'yyyy-mm-dd HH:MM:ss' }"
          :minDate="minDate"
          :maxDate="maxDate"
          :singleDatePicker="true"
          :timePicker="true"
          :timePicker24Hour="false"
          :showWeekNumbers="true"
          :showDropdowns="true"
          :autoApply="true"
          :ranges="false"
          :alwaysShowCalendars="true"
        ></date-range-picker>
        <p v-if="form.schedule_post && form.sent_at_datetime && form.sent_at_datetime.startDate" class="m-2">
          Post will be published at: {{ form.sent_at_datetime.startDate }}
        </p>
      </div>
      <div class="text-center">
        <button id="schedule_post" data-test="schedule_post" :disabled="form.sent_at_datetime.startDate == ''" class="btn btn-primary mt-2 mb-3" style="padding: 0.425rem 1.9rem !important;" @click="submitRequest('send')">
          <div>Schedule Post</div>
        </button>
        <div>
          <span class="text-secondary fw-semibold me-1">Unsure?</span>
          <span class="text-primary fw-bold" style="cursor: pointer;" @click="submitRequest('save')">Save it as draft</span>
        </div>
      </div>
    </b-modal>
    <b-modal id="postPreviewModal" hide-footer title="Post Preview" body-class="p-0" size="lg" @hide="post_preview = false">
      <div v-html="post_preview_html"></div>
    </b-modal>
  </div>
</template>
<script>
import ValidationError from '../../../components/forms/ValidationError.vue';
import { loadProgressBar } from 'axios-progress-bar';
import axios from 'axios';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import Editor from '@tinymce/tinymce-vue';
import moment from 'moment';

export default {
  components: {
    ValidationError,
    DateRangePicker,
    Editor,
  },
  props: {
    network: {
      type: Object,
      default: null,
    },
    post: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      tinyMceApiKey: process.env.VUE_APP_TINYMCE_API_KEY,
      form: this.initialForm(),
      files: [],
      s3Params: {},
      options: {}, // uploader options
      uploadFinished: false,
      minDate: new Date(),
      maxDate: new Date(),
      post_preview: false,
      post_preview_html: '',
      formType: this.post ? 'edit' : 'create',
      postFormErrors: [],
      mail_setting: false,
      home: window.location.origin,
      showScheduleModal: false,
    };
  },
  computed: {
    formErrors() {
      return this.$store.state.validationErrors;
    },
    formUpdated(){
      return ( JSON.stringify(this.form) != JSON.stringify(this.initialForm()) ) ? true : false;
    }
  },
  watch: {
    formUpdated(value) {
      this.$emit('form-updated', value);
    }
  },
  mounted() {
    this.init();
    if (this.post) {
      this.form = this.editData();
    }
  },
  created() {
    this.$bus.on('save-post-draft', () => this.submitRequest('save'));
  },
  methods: {
    utcToLocal: function (dt) {
      return moment.utc(dt).local();
    },
    /**
     * @define Form events
     */
    init() {
      this.maxDate.setDate(new Date());
      this.busy = true;

      this.$http
        .post('s3/upload-params')
        .then(resp => {
          this.setupUploadForm(resp.data);
          // upload setup is done!
          this.busy = false;
        })
        .catch(err => {
          this.$bvModal.msgBoxOk(
            this.$t('CreateMediaWithoutCollection.s3-upload-initialization-failed-please-check-console-for-details-or-refresh-the-page-to-try-again')
          );
          console.log('get s3 upload params failed', err);
          this.busy = false;
        });
    },
    setupUploadForm(s3) {
      // see option documentation here: https://github.com/thetutlage/vue-clip
      this.options = {
        url: s3.formAttributes.action,
        maxFilesize: this.maxFilesize || 500, // 500 MB
        autoProcessQueue: true,
        maxFiles: this.maxFiles || 10,
      };
      if (this.acceptedFiles) this.options.acceptedFiles = this.acceptedFiles;

      this.s3Params = s3;
    },
    initialForm() {
      return {
        title: '',
        content: '',
        cover_image: '',
        author: '',
        is_published: '',
        is_draft: '',
        is_scheduled: '',
        sent_at_datetime: {
          // default is one hour from now
          startDate: new Date(moment().add(1, 'hour').format('YYYY-MM-DD HH:mm:ss')),
          endDate: '',
        },
        schedule_post: false,
      };
    },
    editData() {
      let $post = this.post;
      let startDate = $post.is_scheduled ? new Date(this.utcToLocal($post.scheduled_on).format()) : '';
      let endDate = $post.is_scheduled ? new Date(this.utcToLocal($post.scheduled_on).format()) : '';
      return {
        title: $post.title,
        content: $post.content,
        cover_image: $post.cover_image,
        author: $post.author,
        is_published: $post.is_published,
        is_draft: $post.is_draft,
        is_scheduled: $post.is_scheduled,
        sent_at_datetime: { startDate, endDate },
        schedule_post: !!$post.is_scheduled,
      };
    },
    submitRequest(requestAction) {
      this.$emit('form-updated', false);
      let msg, url, post;
      let isSave = requestAction === 'save';
      this.form.eventAction = isSave ? 'save' : (this.form.schedule_post ? 'schedule' : 'publish');
      this.form.schedule_post =
        this.form.schedule_post === null || this.form.schedule_post === '' || this.form.schedule_post === false ? false : true;
      if (this.form.schedule_post) {
        post = 'Are you sure you want to schedule this post?';
      } else {
        post = 'Are you sure you want to publish this post?';
      }
      msg = !isSave ? post : '';
      url = this.post ? 'networks/blog/post/' + this.post.id : 'networks/' + this.network.slug + '/blog';
      this.form._method = this.formType === 'edit' ? 'PUT' : 'POST';
      this.sendPostAction(isSave, url, msg);
    },
    sendPostAction(isSave, url, msg) {
      if (!isSave) {
        let action = () => {
          this.$http
            .post(url, this.form)
            .then(() => {
              if (this.form.schedule_post) {
                this.form = this.initialForm();
                window.location.href = '/networks/' + this.network.slug + '/blog#scheduled';
              } else {
                this.form = this.initialForm();
                window.location.href = '/networks/' + this.network.slug + '/blog#published';
              }
            })
            .catch(error => {
              this.showServerError(error);
            });
        };
        this.showConfirmPrompt(action, msg);
      } else {
        this.$http
          .post(url, this.form)
          .then(() => {
            window.location.href = '/networks/' + this.network.slug + '/blog#drafts';
          })
          .catch(error => {
            this.showServerError(error);
          });
      }
    },
    /**
     * @define Upload To S3 function for TinyMCE
     */
    async uploadImageTinyMceBlob(blobInfo, success) {
      await this.uploadImage(blobInfo.blob(), success);
    },
    /**
     * @define Upload Image to S3 using Axios
     * @param file
     * @param success
     * @returns {Promise<void>}
     */
    async uploadImage(file, success) {
      let remoteHost = this.s3Params.formAttributes.action;
      var fullPath = file.name;
      var properPath = this.s3Params.uploadPathPrefix + '/' + fullPath;

      var formData = new FormData();
      // update formData before the file is uploaded
      for (let key in this.s3Params.formInputs) {
        var value = this.s3Params.formInputs[key];
        if (key == 'key') value = properPath; // use the proper path calculated (in case its a full folder upload)
        formData.append(key, value);
      }
      this.files.push({
        name: file.name,
        size: file.size,
        fullPath: properPath,
      });
      formData.append('file', file, file.name);
      let axios_instance = axios.create({ headers: { Authorization: '' } });
      loadProgressBar('', axios_instance);
      await axios_instance
        .post(remoteHost, formData)
        .then(() => {
          let attributes = {
            url: remoteHost + '/' + properPath,
            href: remoteHost + '/' + properPath,
          };
          success(attributes.url);
        })
        .catch(err => {
          console.log('get s3 upload params failed', err);
        });
    },
    showPostPreview() {
      this.form.eventAction = 'preview';this.post_preview = true;
      this.$http.post('networks/blog/post/preview', this.form).then(resp => {
        this.post_preview_html = resp.data;

        if (this.post_preview) {
          this.$bvModal.show('postPreviewModal');
        } else {
          this.$bvModal.hide('postPreviewModal');
        }
      });
    },
    uploadCoverImage($event) {
      const file = $event.target.files[0];
      const reader = new FileReader();
      let base64String = '';
      reader.onloadend = () => {
        base64String = reader.result;
        this.$http.post('networks/blog/post/upload-cover', { cover_image: base64String }).then(resp => {
          this.form.cover_image = resp.data.filename;
        });
      };
      reader.readAsDataURL(file);
    },
    /**
     * @define show server error with alert
     */
    showServerError(error) {
      let statusCodes = [403, 404, 422];
      if (!statusCodes.includes(error.response.status)) {
        this.postFormErrors = [JSON.parse(error.request.response).post];
      }
    },
  },
};
</script>